import { CAMERA_SHOTS } from './FlightControls.js';
import { CLOUD_QUALITY_PRESETS, QUALITY_ORDER } from '../clouds/CloudQuality.js';
import { CLOUD_STYLE_PRESETS } from '../clouds/CloudStyles.js';

const GROUPS = [
	{
		name: 'Shape',
		controls: [
			[ 'coverage', 'Coverage', 0.05, 0.95, 0.01 ],
			[ 'density', 'Density', 0.05, 3, 0.01 ],
			[ 'shapeScale', 'Formation frequency', 0.35, 4, 0.01 ],
			[ 'worldScale', 'World pattern scale', 400, 12000, 50 ],
			[ 'weatherScale', 'Weather region scale', 1000, 50000, 250 ],
			[ 'weatherStrength', 'Weather variation', 0, 1.5, 0.01 ],
			[ 'warpScale', 'Warp region scale', 400, 20000, 100 ],
			[ 'warpStrength', 'Domain warp', 0, 1.5, 0.01 ],
			[ 'baseBlend', 'Base decorrelation', 0, 1, 0.01 ],
			[ 'softness', 'Edge softness', 0.01, 0.35, 0.005 ],
			[ 'bottomFade', 'Flat base', 0.01, 0.45, 0.005 ],
			[ 'topFade', 'Top erosion', 0.02, 0.65, 0.005 ],
			[ 'anvil', 'Anvil growth', 0, 1, 0.01 ],
			[ 'detailErosion', 'Detail influence', 0, 1.5, 0.01 ],
			[ 'detailContrast', 'Detail contrast', 0.25, 3, 0.01 ],
			[ 'detailScale', 'Detail scale', 1, 8, 0.05 ],
			[ 'detailBlend', 'Detail decorrelation', 0, 1, 0.01 ],
			[ 'boundaryFade', 'Boundary fade', 0.01, 0.35, 0.005 ],
			[ 'detailFilter', 'Distant detail filter', 0, 1, 0.01 ]
		]
	},
	{
		name: 'Lighting',
		controls: [
			[ 'sunIntensity', 'Sun energy', 0, 12, 0.05 ],
			[ 'extinction', 'Extinction', 0.001, 0.12, 0.001 ],
			[ 'ambientIntensity', 'Ambient fill', 0, 3, 0.01 ],
			[ 'phaseForward', 'Forward scatter', 0, 0.92, 0.01 ],
			[ 'phaseBackward', 'Back scatter', - 0.8, 0, 0.01 ],
			[ 'phaseMix', 'Phase mix', 0, 1, 0.01 ],
			[ 'powder', 'Silver lining', 0, 4, 0.01 ],
			[ 'lightDistance', 'Shadow reach', 0.01, 0.5, 0.01 ],
			[ 'shadowDistribution', 'Shadow near bias', 1, 4, 0.05 ],
			[ 'sunColor', 'Sun color', 'color' ],
			[ 'cloudColor', 'Cloud albedo', 'color' ],
			[ 'ambientTop', 'Sky bounce', 'color' ],
			[ 'ambientBottom', 'Ground bounce', 'color' ]
		]
	},
	{
		name: 'Motion',
		controls: [
			[ 'windDirection', 'Wind direction', 0, 360, 1 ],
			[ 'windSpeed', 'Wind speed', 0, 0.2, 0.001 ],
			[ 'turbulenceSpeed', 'Detail drift', 0, 0.08, 0.001 ],
			[ 'formationStrength', 'Formation amount', 0, 1, 0.01 ],
			[ 'formationSpeed', 'Formation rate', 0, 0.5, 0.005 ],
			[ 'roilStrength', 'Roil amount', 0, 1.5, 0.01 ],
			[ 'roilSpeed', 'Roil rate', 0, 1, 0.01 ],
			[ 'frozen', 'Freeze field', 'checkbox' ]
		]
	},
	{
		name: 'Ray budget',
		controls: [
			[ 'viewSteps', 'View steps', 16, 220, 1 ],
			[ 'lightSteps', 'Light steps', 1, 12, 1 ],
			[ 'detailOctaves', 'Detail samples', 1, 3, 1 ],
			[ 'renderScale', 'Cloud resolution', 0.1, 1, 0.01 ],
			[ 'jitterStrength', 'Jitter', 0, 1, 0.01 ],
			[ 'animatedJitter', 'Animate jitter', 'checkbox' ],
			[ 'earlyExit', 'Early exit', 0.001, 0.2, 0.001 ]
		]
	}
];

const ATMOSPHERE_CONTROLS = [
	[ 'automaticLighting', 'Automatic lighting', 'checkbox' ],
	[ 'sunAzimuth', 'Sun azimuth', 0, 360, 1 ],
	[ 'sunElevation', 'Sun elevation', - 18, 90, 0.5 ],
	[ 'sunsetStartElevation', 'Sunset starts at', 2, 30, 0.5 ],
	[ 'sunsetWarmth', 'Sunset warmth', 0, 2, 0.01 ],
	[ 'sunsetBrightness', 'Sunset brightness boost', 0, 4, 0.05 ],
	[ 'turbidity', 'Aerosol / haze', 1, 10, 0.05 ],
	[ 'rayleigh', 'Rayleigh density', 0.1, 4, 0.01 ],
	[ 'mieCoefficient', 'Mie density', 0.0001, 0.03, 0.0001 ],
	[ 'mieDirectionalG', 'Mie direction', 0.5, 0.95, 0.005 ],
	[ 'ozone', 'Ozone', 0, 2, 0.01 ],
	[ 'multipleScattering', 'Multi scattering', 0, 1.5, 0.01 ],
	[ 'groundAlbedo', 'Ground albedo', 'color' ],
	[ 'skyIntensity', 'Sky radiance', 0.1, 3, 0.01 ],
	[ 'sunIntensity', 'Solar radiance', 0, 4, 0.01 ],
	[ 'aerialDensity', 'Distance haze', 0, 0.004, 0.00001 ],
	[ 'autoExposure', 'Eye adaptation', 'checkbox' ],
	[ 'exposure', 'Base exposure', 0.25, 3, 0.01 ],
	[ 'animateTime', 'Animate sun', 'checkbox' ],
	[ 'timeScale', 'Sun speed', 0, 8, 0.05 ]
];

const TEMPORAL_CONTROLS = [
	[ 'enabled', 'Temporal stability', 'checkbox' ],
	[ 'historyWeight', 'History weight', 0, 0.96, 0.01 ],
	[ 'depthThreshold', 'Depth rejection', 2, 80, 1 ],
	[ 'alphaThreshold', 'Opacity rejection', 0.02, 0.5, 0.01 ],
	[ 'motionRejection', 'Motion response', 8, 160, 1 ],
	[ 'depthSharpness', 'Depth edge filter', 0.005, 0.25, 0.005 ],
	[ 'detailBlur', 'Low-res detail blur', 0, 4, 0.05 ],
	[ 'clampExpansion', 'History clamp', 0, 0.3, 0.01 ],
	[ 'occlusionSoftness', 'Occlusion softness', 0.25, 12, 0.25 ]
];

const CLOUD_SHADOW_CONTROLS = [
	[ 'enabled', 'Terrain cloud shadows', 'checkbox' ],
	[ 'strength', 'Shadow strength', 0, 1, 0.01 ],
	[ 'distance', 'Shadow coverage', 1000, 12000, 100 ],
	[ 'resolution', 'Shadow resolution', 64, 1024, 64 ],
	[ 'samples', 'Shadow ray samples', 2, 32, 1 ],
	[ 'softness', 'Shadow softness', 0, 8, 0.05 ],
	[ 'density', 'Terrain shadow density', 0.05, 2, 0.01 ],
	[ 'minimumLight', 'Minimum shadow light', 0, 0.9, 0.01 ],
	[ 'gamma', 'Shadow gamma', 0.2, 2, 0.01 ]
];

export const SETTINGS_CONTROL_KEYS = Object.freeze( {
	cloud: Object.freeze( [
		'quality',
		'debugMode',
		...GROUPS.flatMap( ( group ) => group.controls.map( ( control ) => control[ 0 ] ) )
	] ),
	atmosphere: Object.freeze( ATMOSPHERE_CONTROLS.map( ( control ) => control[ 0 ] ) ),
	temporal: Object.freeze( TEMPORAL_CONTROLS.map( ( control ) => control[ 0 ] ) ),
	cloudShadows: Object.freeze( CLOUD_SHADOW_CONTROLS.map( ( control ) => control[ 0 ] ) ),
	adaptive: Object.freeze( [ 'enabled' ] ),
	resources: Object.freeze( [ 'seed', 'baseResolution', 'detailResolution' ] )
} );

const TIME_PRESETS = Object.freeze( {
	dawn: { label: 'Dawn', sunElevation: 3 },
	day: { label: 'Day', sunElevation: 38 },
	golden: { label: 'Golden', sunElevation: 10 },
	sunset: { label: 'Sunset', sunElevation: 0 },
	twilight: { label: 'Twilight', sunElevation: - 6 }
} );

function formatValue( value, step ) {

	if ( typeof value !== 'number' ) return String( value );
	if ( step >= 1 ) return value.toFixed( 0 );
	if ( step >= 0.01 ) return value.toFixed( 2 );
	if ( step >= 0.001 ) return value.toFixed( 3 );
	if ( step >= 0.0001 ) return value.toFixed( 4 );
	return value.toFixed( 5 );

}

export class CloudControlPanel {

	constructor( options ) {

		this.clouds = options.clouds;
		this.atmosphere = options.atmosphere;
		this.cloudPipeline = options.cloudPipeline;
		this.cloudShadows = options.cloudShadows;
		this.adaptive = options.adaptive;
		this.onRenderScale = options.onRenderScale;
		this.onQuality = options.onQuality;
		this.onShot = options.onShot;
		this.onAtmosphereChange = options.onAtmosphereChange ?? ( () => {} );
		this.onSaveDefaults = options.onSaveDefaults ?? ( () => false );
		this.onClearDefaults = options.onClearDefaults ?? ( () => false );
		this.onSaveProjectDefaults = options.onSaveProjectDefaults ?? ( async () => false );
		this.onResetProjectDefaults = options.onResetProjectDefaults ?? ( async () => false );
		this.onExportDefaults = options.onExportDefaults ?? ( () => null );
		this.onImportDefaults = options.onImportDefaults ?? ( () => false );
		this.controls = new Map();
		this.atmosphereControls = new Map();
		this.temporalControls = new Map();
		this.shadowControls = new Map();
		this.metrics = {};
		this.element = document.createElement( 'aside' );
		this.element.className = 'cloud-panel';
		this.element.innerHTML = `
			<header class="panel-header">
				<div><span class="eyebrow">WEBGPU / TSL LAB</span><h1>CyborgClouds</h1></div>
				<button class="panel-toggle" type="button" aria-label="Toggle controls">×</button>
			</header>
			<div class="panel-scroll">
				<section class="live-card">
					<div><span>FRAME</span><strong data-metric="frame">--</strong></div>
					<div><span>MODE</span><strong data-metric="mode">BAL</strong></div>
					<div><span>ALT</span><strong data-metric="altitude">--</strong></div>
					<div><span>SUN</span><strong data-metric="sun">--</strong></div>
				</section>
				<section class="preset-section"><div class="section-title"><span>Quality</span><label><input data-adaptive type="checkbox"> Adaptive</label></div><div class="preset-grid"></div></section>
				<section class="defaults-section">
					<div><strong>DEFAULTS</strong><small>Saving a project baseline captures every setting and clears the browser override</small></div>
					<div class="defaults-actions">
						<button type="button" data-save-defaults>Save browser override</button>
						<button type="button" data-clear-defaults>Clear browser override</button>
						<button type="button" data-save-project-defaults>Save as project baseline</button>
						<button type="button" data-reset-project-defaults>Reset project baseline</button>
						<button type="button" data-export-defaults>Export preset</button>
						<button type="button" data-import-defaults>Import preset</button>
						<input data-import-defaults-file type="file" accept="application/json,.json" hidden>
					</div>
				</section>
				<section class="time-section"><div class="section-title"><span>Time of day</span><small>AUTO COLOR</small></div><div class="time-grid"></div></section>
				<section class="look-section"><div class="section-title"><span>Weather looks</span></div><div class="look-grid"></div></section>
				<div class="groups"></div>
				<section class="control-group debug-group">
					<button data-debug="0" class="debug-button active">Beauty</button>
					<button data-debug="1" class="debug-button">Density</button>
					<button data-debug="2" class="debug-button">Transmit</button>
					<button data-debug="3" class="debug-button">Light</button>
				</section>
				<section class="compute-card"><span>COMPUTE FIELD</span><strong data-metric="compute">--</strong><small data-metric="memory">--</small><button data-regenerate>Regenerate seed</button></section>
				<section class="compute-card atmosphere-compute-card"><span>ATMOSPHERE LUTS</span><strong data-metric="atmosphere">--</strong><small data-metric="atmosphere-memory">--</small></section>
			</div>
		`;

		document.body.appendChild( this.element );
		this.#buildPresets();
		this.#buildTimePresets();
		this.#buildLooks();
		this.#buildControls();
		this.#buildShots();
		this.#bind();
		this.refresh();

	}

	#buildTimePresets() {

		const grid = this.element.querySelector( '.time-grid' );
		for ( const [ name, preset ] of Object.entries( TIME_PRESETS ) ) {

			const button = document.createElement( 'button' );
			button.type = 'button';
			button.dataset.time = name;
			button.textContent = preset.label;
			grid.appendChild( button );

		}

	}

	#buildLooks() {

		const grid = this.element.querySelector( '.look-grid' );

		for ( const [ name, preset ] of Object.entries( CLOUD_STYLE_PRESETS ) ) {

			const button = document.createElement( 'button' );
			button.type = 'button';
			button.dataset.style = name;
			button.textContent = preset.label;
			grid.appendChild( button );

		}

	}

	#buildPresets() {

		const grid = this.element.querySelector( '.preset-grid' );

		for ( const name of QUALITY_ORDER ) {

			const preset = CLOUD_QUALITY_PRESETS[ name ];
			const button = document.createElement( 'button' );
			button.type = 'button';
			button.dataset.quality = name;
			button.innerHTML = `<strong>${ preset.label }</strong><small>${ preset.viewSteps } × ${ preset.lightSteps }</small>`;
			grid.appendChild( button );

		}

	}

	#buildControls() {

		const groups = this.element.querySelector( '.groups' );
		this.#buildAtmosphereControls( groups );
		this.#buildShadowControls( groups );
		this.#buildTemporalControls( groups );

		for ( const group of GROUPS ) {

			const details = document.createElement( 'details' );
			details.className = 'control-group';
			details.open = group.name === 'Shape';
			details.innerHTML = `<summary>${ group.name }<span>${ group.controls.length }</span></summary><div class="control-list"></div>`;
			const list = details.querySelector( '.control-list' );

			for ( const descriptor of group.controls ) {

				const [ key, label, typeOrMin, max, step ] = descriptor;
				const row = document.createElement( 'label' );
				row.className = `control-row control-${ typeOrMin }`;
				const labelText = document.createElement( 'span' );
				labelText.textContent = label;
				const isSpecial = typeof typeOrMin === 'string';
				const input = document.createElement( 'input' );
				input.dataset.parameter = key;
				input.type = isSpecial ? typeOrMin : 'range';
				input.setAttribute( 'aria-label', label );
				input.title = label;
				row.append( labelText );

				if ( ! isSpecial ) {

					input.min = typeOrMin;
					input.max = max;
					input.step = step;
					const value = document.createElement( 'output' );
					value.dataset.output = key;
					row.append( input, value );

				} else {

					row.append( input );

				}

				this.controls.set( key, { input, step: step ?? 0 } );
				list.appendChild( row );

			}

			groups.appendChild( details );

		}

	}

	#buildShadowControls( groups ) {

		if ( ! this.cloudShadows ) return;
		const details = document.createElement( 'details' );
		details.className = 'control-group cloud-shadow-group';
		details.open = true;
		details.innerHTML = `<summary>Terrain shadows<span>${ CLOUD_SHADOW_CONTROLS.length }</span></summary><div class="control-list"></div>`;
		const list = details.querySelector( '.control-list' );

		for ( const [ key, label, typeOrMin, max, step ] of CLOUD_SHADOW_CONTROLS ) {

			const row = document.createElement( 'label' );
			row.className = `control-row control-${ typeOrMin }`;
			const labelText = document.createElement( 'span' );
			labelText.textContent = label;
			const input = document.createElement( 'input' );
			input.dataset.cloudShadow = key;
			const isSpecial = typeof typeOrMin === 'string';
			input.type = isSpecial ? typeOrMin : 'range';
			input.setAttribute( 'aria-label', label );
			input.title = label;
			row.append( labelText );
			if ( ! isSpecial ) {

				input.min = typeOrMin;
				input.max = max;
				input.step = step;
				const value = document.createElement( 'output' );
				value.dataset.cloudShadowOutput = key;
				row.append( input, value );

			} else row.append( input );
			this.shadowControls.set( key, { input, step: step ?? 0 } );
			list.appendChild( row );

		}
		groups.appendChild( details );

	}

	#buildTemporalControls( groups ) {

		if ( ! this.cloudPipeline ) return;
		const details = document.createElement( 'details' );
		details.className = 'control-group temporal-group';
		details.open = true;
		details.innerHTML = `<summary>Temporal resolve<span>${ TEMPORAL_CONTROLS.length }</span></summary><div class="control-list"></div>`;
		const list = details.querySelector( '.control-list' );

		for ( const descriptor of TEMPORAL_CONTROLS ) {

			const [ key, label, typeOrMin, max, step ] = descriptor;
			const row = document.createElement( 'label' );
			row.className = `control-row control-${ typeOrMin }`;
			const labelText = document.createElement( 'span' );
			labelText.textContent = label;
			const input = document.createElement( 'input' );
			input.dataset.temporal = key;
			const isSpecial = typeof typeOrMin === 'string';
			input.type = isSpecial ? typeOrMin : 'range';
			input.setAttribute( 'aria-label', label );
			input.title = label;
			row.append( labelText );

			if ( ! isSpecial ) {

				input.min = typeOrMin;
				input.max = max;
				input.step = step;
				const value = document.createElement( 'output' );
				value.dataset.temporalOutput = key;
				row.append( input, value );

			} else {

				row.append( input );

			}

			this.temporalControls.set( key, { input, step: step ?? 0 } );
			list.appendChild( row );

		}

		groups.appendChild( details );

	}

	#buildAtmosphereControls( groups ) {

		if ( ! this.atmosphere ) return;
		const details = document.createElement( 'details' );
		details.className = 'control-group atmosphere-group';
		details.open = true;
		details.innerHTML = `<summary>Atmosphere<span>${ ATMOSPHERE_CONTROLS.length }</span></summary><div class="control-list"></div>`;
		const list = details.querySelector( '.control-list' );

		for ( const descriptor of ATMOSPHERE_CONTROLS ) {

			const [ key, label, typeOrMin, max, step ] = descriptor;
			const row = document.createElement( 'label' );
			row.className = `control-row control-${ typeOrMin }`;
			const labelText = document.createElement( 'span' );
			labelText.textContent = label;
			const input = document.createElement( 'input' );
			input.dataset.atmosphere = key;
			const isSpecial = typeof typeOrMin === 'string';
			input.type = isSpecial ? typeOrMin : 'range';
			input.setAttribute( 'aria-label', label );
			input.title = label;
			row.append( labelText );

			if ( ! isSpecial ) {

				input.min = typeOrMin;
				input.max = max;
				input.step = step;
				const value = document.createElement( 'output' );
				value.dataset.atmosphereOutput = key;
				row.append( input, value );

			} else {

				row.append( input );

			}

			this.atmosphereControls.set( key, { input, step: step ?? 0 } );
			list.appendChild( row );

		}

		groups.appendChild( details );

	}

	#buildShots() {

		const dock = document.createElement( 'nav' );
		dock.className = 'shot-dock';
		dock.innerHTML = `<span>CAMERA</span>${ Object.entries( CAMERA_SHOTS ).map( ( [ key, shot ] ) => `<button data-shot="${ key }">${ shot.label }</button>` ).join( '' ) }`;
		document.body.appendChild( dock );
		this.shotDock = dock;

	}

	#bind() {

		const importInput = this.element.querySelector( '[data-import-defaults-file]' );
		importInput.addEventListener( 'change', async () => {

			const file = importInput.files?.[ 0 ];
			if ( ! file ) return;
			const button = this.element.querySelector( '[data-import-defaults]' );
			try {

				const imported = this.onImportDefaults( JSON.parse( await file.text() ) );
				button.textContent = imported ? 'Imported — reloading' : 'Could not import';
				if ( imported ) window.location.reload();

			} catch {

				button.textContent = 'Invalid preset JSON';

			}
			window.setTimeout( () => { button.textContent = 'Import preset'; }, 1800 );
			importInput.value = '';

		} );

		this.element.addEventListener( 'input', ( event ) => {

			const cloudShadowKey = event.target.dataset.cloudShadow;
			if ( cloudShadowKey && this.cloudShadows ) {

				const value = event.target.type === 'checkbox' ? event.target.checked : Number( event.target.value );
				this.cloudShadows.setParameters( { [ cloudShadowKey ]: value } );
				this.refresh();

			}

			const temporalKey = event.target.dataset.temporal;
			if ( temporalKey && this.cloudPipeline ) {

				const value = event.target.type === 'checkbox' ? event.target.checked : Number( event.target.value );
				if ( temporalKey === 'occlusionSoftness' ) this.cloudPipeline.setReconstruction( { occlusionSoftness: value } );
				else this.cloudPipeline.setTemporalOptions( { [ temporalKey ]: value } );
				this.refresh();

			}

			const atmosphereKey = event.target.dataset.atmosphere;
			if ( atmosphereKey && this.atmosphere ) {

				const value = event.target.type === 'checkbox' ? event.target.checked : event.target.type === 'color' ? event.target.value : Number( event.target.value );
				this.atmosphere.setParameters( { [ atmosphereKey ]: value } );
				this.onAtmosphereChange( atmosphereKey, value );
				this.refresh();

			}

			const key = event.target.dataset.parameter;
			if ( key ) {

				const value = event.target.type === 'checkbox' ? event.target.checked : event.target.type === 'color' ? event.target.value : Number( event.target.value );
				this.clouds.setParameters( { [ key ]: value } );
				this.cloudPipeline?.resetTemporal();
				this.activeStyle = null;
				if ( key === 'renderScale' ) this.onRenderScale( value );
				this.refresh();

			}

			if ( event.target.matches( '[data-adaptive]' ) ) {

				this.adaptive.enabled = event.target.checked;

			}

		} );

		this.element.addEventListener( 'click', async ( event ) => {

			const quality = event.target.closest( '[data-quality]' )?.dataset.quality;
			if ( quality ) {

				this.adaptive.setQuality( quality );
				this.clouds.setQuality( quality );
				this.atmosphere?.setQuality( quality );
				this.onQuality( quality );
				this.refresh();

			}

			const time = event.target.closest( '[data-time]' )?.dataset.time;
			if ( time && this.atmosphere ) {

				this.atmosphere.setParameters( TIME_PRESETS[ time ] );
				this.onAtmosphereChange( 'sunElevation', TIME_PRESETS[ time ].sunElevation );
				this.refresh();

			}

			const debugMode = event.target.closest( '[data-debug]' )?.dataset.debug;
			if ( debugMode !== undefined ) {

				this.clouds.setParameters( { debugMode: Number( debugMode ) } );
				this.cloudPipeline?.resetTemporal();
				this.refresh();

			}

			const style = event.target.closest( '[data-style]' )?.dataset.style;
			if ( style ) {

				const { label, ...parameters } = CLOUD_STYLE_PRESETS[ style ];
				this.clouds.setParameters( parameters );
				this.cloudPipeline?.resetTemporal();
				this.activeStyle = style;
				this.refresh();

			}

			if ( event.target.closest( '[data-regenerate]' ) ) {

				const button = event.target.closest( '[data-regenerate]' );
				button.disabled = true;
				button.textContent = 'Computing…';
				await this.clouds.regenerateNoise();
				button.disabled = false;
				button.textContent = 'Regenerate seed';
				this.setComputeStats( this.clouds.getStats() );

			}

			if ( event.target.closest( '[data-save-defaults]' ) ) {

				const button = event.target.closest( '[data-save-defaults]' );
				const saved = this.onSaveDefaults();
				button.textContent = saved ? 'Browser override saved' : 'Could not save';
				window.setTimeout( () => { button.textContent = 'Save browser override'; }, 1800 );

			}

			if ( event.target.closest( '[data-clear-defaults]' ) ) {

				const button = event.target.closest( '[data-clear-defaults]' );
				const cleared = this.onClearDefaults();
				button.textContent = cleared ? 'Override cleared — reloading' : 'Could not clear';
				if ( cleared ) window.setTimeout( () => window.location.reload(), 250 );
				window.setTimeout( () => { button.textContent = 'Clear browser override'; }, 1800 );

			}

			if ( event.target.closest( '[data-save-project-defaults]' ) ) {

				const button = event.target.closest( '[data-save-project-defaults]' );
				button.disabled = true;
				button.textContent = 'Writing project baseline…';
				const saved = await this.onSaveProjectDefaults();
				button.textContent = saved ? 'Baseline saved — reloading' : 'Development server required';
				if ( saved ) window.setTimeout( () => window.location.reload(), 250 );
				else {

					button.disabled = false;
					window.setTimeout( () => { button.textContent = 'Save as project baseline'; }, 2000 );

				}

			}

			if ( event.target.closest( '[data-reset-project-defaults]' ) ) {

				const button = event.target.closest( '[data-reset-project-defaults]' );
				button.disabled = true;
				button.textContent = 'Resetting project baseline…';
				const reset = await this.onResetProjectDefaults();
				button.textContent = reset ? 'Baseline reset — reloading' : 'Development server required';
				if ( reset ) window.setTimeout( () => window.location.reload(), 250 );
				else {

					button.disabled = false;
					window.setTimeout( () => { button.textContent = 'Reset project baseline'; }, 2000 );

				}

			}

			if ( event.target.closest( '[data-export-defaults]' ) ) {

				const defaults = this.onExportDefaults();
				if ( defaults ) {

					const url = URL.createObjectURL( new Blob( [ `${ JSON.stringify( defaults, null, 2 ) }\n` ], { type: 'application/json' } ) );
					const anchor = document.createElement( 'a' );
					anchor.href = url;
					anchor.download = 'cyborg-clouds-preset.json';
					anchor.click();
					window.setTimeout( () => URL.revokeObjectURL( url ), 0 );

				}

			}

			if ( event.target.closest( '[data-import-defaults]' ) ) {

				importInput.click();

			}

			if ( event.target.closest( '.panel-toggle' ) ) {

				this.element.classList.toggle( 'is-collapsed' );
				this.element.querySelector( '.panel-toggle' ).textContent = this.element.classList.contains( 'is-collapsed' ) ? '☰' : '×';

			}

		} );

		this.shotDock.addEventListener( 'click', ( event ) => {

			const shot = event.target.closest( '[data-shot]' )?.dataset.shot;
			if ( shot ) this.onShot( shot );

		} );

	}

	refresh() {

		const parameters = this.clouds.parameters;

		for ( const [ key, control ] of this.controls ) {

			const value = parameters[ key ];
			if ( control.input.type === 'checkbox' ) control.input.checked = Boolean( value );
			else control.input.value = value;

			const output = this.element.querySelector( `[data-output="${ key }"]` );
			if ( output ) output.value = formatValue( value, control.step );

		}

		if ( this.atmosphere ) {

			for ( const [ key, control ] of this.atmosphereControls ) {

				const value = this.atmosphere.parameters[ key ];
				if ( control.input.type === 'checkbox' ) control.input.checked = Boolean( value );
				else control.input.value = value;
				const output = this.element.querySelector( `[data-atmosphere-output="${ key }"]` );
				if ( output ) output.value = formatValue( value, control.step );

			}

			const automatic = this.atmosphere.parameters.automaticLighting;
			for ( const key of [ 'sunIntensity', 'sunColor', 'ambientTop', 'ambientBottom' ] ) {

				const control = this.controls.get( key );
				if ( control ) control.input.disabled = automatic;

			}

			for ( const button of this.element.querySelectorAll( '[data-time]' ) ) {

				const target = TIME_PRESETS[ button.dataset.time ].sunElevation;
				button.classList.toggle( 'active', Math.abs( this.atmosphere.parameters.sunElevation - target ) < 0.26 );

			}

		}

		if ( this.cloudPipeline ) {

			const temporal = this.cloudPipeline.getTemporalOptions();
			for ( const [ key, control ] of this.temporalControls ) {

				const value = temporal[ key ];
				if ( control.input.type === 'checkbox' ) control.input.checked = Boolean( value );
				else control.input.value = value;
				const output = this.element.querySelector( `[data-temporal-output="${ key }"]` );
				if ( output ) output.value = formatValue( value, control.step );

			}

		}

		if ( this.cloudShadows ) {

			for ( const [ key, control ] of this.shadowControls ) {

				const value = this.cloudShadows.parameters[ key ];
				if ( control.input.type === 'checkbox' ) control.input.checked = Boolean( value );
				else control.input.value = value;
				const output = this.element.querySelector( `[data-cloud-shadow-output="${ key }"]` );
				if ( output ) output.value = formatValue( value, control.step );

			}

		}

		for ( const button of this.element.querySelectorAll( '[data-quality]' ) ) {

			button.classList.toggle( 'active', button.dataset.quality === parameters.quality );

		}

		for ( const button of this.element.querySelectorAll( '[data-debug]' ) ) {

			button.classList.toggle( 'active', Number( button.dataset.debug ) === parameters.debugMode );

		}

		for ( const button of this.element.querySelectorAll( '[data-style]' ) ) {

			button.classList.toggle( 'active', button.dataset.style === this.activeStyle );

		}

		this.element.querySelector( '[data-metric="mode"]' ).textContent = parameters.quality.slice( 0, 3 ).toUpperCase();

	}

	setLiveMetrics( frameMs, altitude, sunElevation = null ) {

		this.element.querySelector( '[data-metric="frame"]' ).textContent = `${ frameMs.toFixed( 1 ) }ms`;
		this.element.querySelector( '[data-metric="altitude"]' ).textContent = `${ altitude.toFixed( 0 ) }m`;
		this.element.querySelector( '[data-metric="sun"]' ).textContent = sunElevation === null ? '--' : `${ sunElevation.toFixed( 1 ) }°`;

	}

	setAtmosphereStats( stats ) {

		this.element.querySelector( '[data-metric="atmosphere"]' ).textContent = `${ stats.skyWidth }×${ stats.skyHeight } + ${ stats.transmittanceWidth }×${ stats.transmittanceHeight }`;
		this.element.querySelector( '[data-metric="atmosphere-memory"]' ).textContent = `${ ( stats.approximateTextureBytes / 1048576 ).toFixed( 2 ) } MB · dispatch ${ stats.lastComputeMs.toFixed( 1 ) } ms`;

	}

	setComputeStats( stats ) {

		this.element.querySelector( '[data-metric="compute"]' ).textContent = `${ stats.baseResolution }³ + ${ stats.detailResolution }³`;
		this.element.querySelector( '[data-metric="memory"]' ).textContent = `${ ( stats.approximateTextureBytes / 1048576 ).toFixed( 1 ) } MB · seed ${ stats.seed } · dispatch ${ stats.lastComputeMs.toFixed( 0 ) } ms`;

	}

}
